import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { StateBadge } from '@/components/StateBadge';
import {
  PendingInputPanel,
  initialApprovalDraft,
  type ApprovalDraftState,
} from '@/components/PendingInputPanel';
import { DeleteTaskDialog } from '@/components/DeleteTaskDialog';
import { ReopenTaskDialog } from '@/components/ReopenTaskDialog';
import { Timeline, type PlanEdit } from '@/components/Timeline';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { isWorkState, type TaskDetail, type WorkState } from '@agentflow/shared';

export function TaskDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  // Poll faster while the task is actively being worked (especially by AI),
  // slower when it's settled. Fast polling gives a near-live feel as the AI
  // chain advances through triage → plan → execute.
  const query = useQuery({
    queryKey: ['task', id],
    queryFn: () => api.getTask(id!),
    enabled: !!id,
    refetchInterval: (q) => {
      const t = q.state.data?.task;
      if (!t) return 3000;
      const inFlight =
        (t.state === 'triage' || t.state === 'plan' || t.state === 'execute') &&
        t.current_actor === 'ai';
      return inFlight ? 1000 : 3000;
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['task', id] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
    qc.invalidateQueries({ queryKey: ['pending'] });
  };

  // Approval form draft state. Lifted here so the in-place plan editor in
  // the Timeline can be driven by the PendingInputPanel's mode buttons.
  //
  // Note: we deliberately seed `editedPlan` from the latest `ai_output(plan)`
  // event in the task's history rather than from `pending_input.payload.
  // proposed_plan`. The pause payload was originally a placeholder and is
  // not always populated, but the most-recent plan event always reflects
  // exactly what the Timeline is rendering — which is also exactly what the
  // user is about to edit in place.
  const pendingApprovalId =
    query.data?.task.pending_input?.reason === 'approval'
      ? query.data.task.pending_input.id
      : null;
  const latestPlanContent = (() => {
    const events = query.data?.task.recent_events ?? [];
    // recent_events is newest-first, so the first matching event is the latest plan.
    for (const ev of events) {
      if (ev.type !== 'ai_output') continue;
      const payload = (ev.payload ?? {}) as Record<string, unknown>;
      if (payload.kind === 'plan' && typeof payload.content === 'string') {
        return payload.content;
      }
    }
    return '';
  })();
  const [approvalDraft, setApprovalDraft] = useState<ApprovalDraftState>(() =>
    initialApprovalDraft(latestPlanContent),
  );
  const [seededFor, setSeededFor] = useState<string | null>(null);
  useEffect(() => {
    if (pendingApprovalId && pendingApprovalId !== seededFor) {
      setApprovalDraft(initialApprovalDraft(latestPlanContent));
      setSeededFor(pendingApprovalId);
    } else if (!pendingApprovalId && seededFor !== null) {
      setSeededFor(null);
    }
  }, [pendingApprovalId, latestPlanContent, seededFor]);

  const completeWork = useMutation({
    mutationFn: () => api.completeWork(id!),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: () => api.cancelTask(id!),
    onSuccess: invalidate,
  });
  const retry = useMutation({
    mutationFn: (resumeFrom: WorkState) => api.retryTask(id!, { resume_from: resumeFrom }),
    onSuccess: invalidate,
  });
  const fail = useMutation({
    mutationFn: () => api.failTask(id!, { reason: 'unrecoverable_error' }),
    onSuccess: invalidate,
  });

  if (query.isLoading) return <div>Loading…</div>;
  if (query.isError || !query.data) return <div>Failed to load task.</div>;

  const task = query.data.task;
  const isTerminal = ['succeeded', 'failed', 'cancelled'].includes(task.state);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/tasks" className="hover:underline">
              Tasks
            </Link>
            <span>/</span>
            <span className="font-mono text-xs">{task.id.slice(0, 8)}</span>
          </div>
          <h2 className="text-2xl font-semibold tracking-tight mt-1">{task.name}</h2>
          {task.description && (
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
              {task.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {task.current_actor && <Badge variant="outline">{task.current_actor}</Badge>}
          <StateBadge state={task.state} pauseReason={task.pause_reason} />
        </div>
      </div>

      {/* AI activity indicator — shows while a work state is being driven by AI. */}
      {(task.state === 'triage' || task.state === 'plan' || task.state === 'execute') &&
        task.current_actor === 'ai' && (
          <Card className="border-sky-300/50 bg-sky-50/30">
            <CardContent className="pt-4 flex items-center gap-3 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-sky-700" />
              <span>
                AI is working on <span className="font-medium">{task.state}</span>… this view
                refreshes automatically.
              </span>
            </CardContent>
          </Card>
        )}

      {/* Chronological timeline of triage classifications, clarifications,
          plans, and the final result. Replaces the older split panels.

          When an approval pause is open and the user clicked "Edit Manually"
          in the form below, the most recent plan card switches to a textarea
          bound to the lifted `approvalDraft.editedPlan` state. */}
      <Timeline
        events={task.recent_events}
        planEdit={
          task.pending_input?.reason === 'approval' && approvalDraft.mode === 'editing'
            ? ({
                value: approvalDraft.editedPlan,
                onChange: (next) => setApprovalDraft((s) => ({ ...s, editedPlan: next })),
              } satisfies PlanEdit)
            : null
        }
      />

      {task.pending_input && (
        <PendingInputPanel
          taskId={task.id}
          pending={task.pending_input}
          onResolved={invalidate}
          approval={
            task.pending_input.reason === 'approval'
              ? { state: approvalDraft, onStateChange: setApprovalDraft }
              : undefined
          }
        />
      )}

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Profile">{task.profile_slug}</Field>
            <Field label="Domain">{task.domain}</Field>
            <Field label="Priority">{task.priority}</Field>
            <Field label="Owner">{task.owner_id}</Field>
            <Field label="Assignee">{task.assignee_id ?? '—'}</Field>
            <Field label="Reviewer">{task.reviewer_id ?? '—'}</Field>
            <Field label="Created">
              {format(new Date(task.created_at), 'PPp')} (
              {formatDistanceToNow(new Date(task.created_at), { addSuffix: true })})
            </Field>
            <Field label="Updated">
              {formatDistanceToNow(new Date(task.updated_at), { addSuffix: true })}
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          {isWorkState(task.state) && (
            <Button onClick={() => completeWork.mutate()} disabled={completeWork.isPending}>
              Mark {task.state} complete
            </Button>
          )}
          {!isTerminal && (
            <>
              <Button variant="outline" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                Cancel task
              </Button>
              <Button
                variant="destructive"
                onClick={() => fail.mutate()}
                disabled={fail.isPending}
                title="Mark task as failed (testing)"
              >
                Mark failed
              </Button>
            </>
          )}
          {task.state === 'failed' && (
            <>
              <Button onClick={() => retry.mutate('triage')} disabled={retry.isPending}>
                Retry from triage
              </Button>
              <Button onClick={() => retry.mutate('plan')} disabled={retry.isPending}>
                Retry from plan
              </Button>
              <Button onClick={() => retry.mutate('execute')} disabled={retry.isPending}>
                Retry from execute
              </Button>
            </>
          )}
          {(task.state === 'succeeded' || task.state === 'cancelled') && (
            <ReopenTaskDialog
              taskId={task.id}
              taskName={task.name}
              currentState={task.state}
              onReopened={invalidate}
            />
          )}
          <div className="ml-auto">
            <DeleteTaskDialog
              taskId={task.id}
              taskName={task.name}
              onDeleted={() => {
                qc.invalidateQueries({ queryKey: ['tasks'] });
                qc.invalidateQueries({ queryKey: ['pending'] });
                qc.invalidateQueries({ queryKey: ['audit'] });
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="events">
        <TabsList>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="context">Context ({task.context.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="events">
          <EventsList task={task} />
        </TabsContent>
        <TabsContent value="context">
          <ContextList task={task} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function EventsList({ task }: { task: TaskDetail }): JSX.Element {
  if (task.recent_events.length === 0) {
    return <Card><CardContent className="pt-6 text-sm text-muted-foreground">No events yet.</CardContent></Card>;
  }
  return (
    <Card>
      <ul className="divide-y">
        {task.recent_events.map((ev) => (
          <li key={ev.id} className="px-4 py-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{ev.type}</Badge>
              {ev.state && <Badge variant="secondary">{ev.state}</Badge>}
              <Badge variant="outline">{ev.actor}</Badge>
              <span className="ml-auto text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(ev.created_at), { addSuffix: true })}
              </span>
            </div>
            {ev.payload_preview && (
              <pre className="mt-2 text-xs bg-muted/50 p-2 rounded overflow-x-auto">
                {ev.payload_preview}
              </pre>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ContextList({ task }: { task: TaskDetail }): JSX.Element {
  if (task.context.length === 0) {
    return <Card><CardContent className="pt-6 text-sm text-muted-foreground">No context attached.</CardContent></Card>;
  }
  return (
    <Card>
      <ul className="divide-y">
        {task.context.map((cx) => (
          <li key={cx.id} className="px-4 py-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant="outline">{cx.kind}</Badge>
              <Badge variant="secondary">{cx.role}</Badge>
              <span className="font-mono text-xs">{cx.ref}</span>
            </div>
            {Object.keys(cx.metadata).length > 0 && (
              <pre className="mt-2 text-xs bg-muted/50 p-2 rounded overflow-x-auto">
                {JSON.stringify(cx.metadata, null, 2)}
              </pre>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
